import clsx from 'clsx';
import React, {
  useRef,
  useEffect,
  ChangeEvent,
  KeyboardEvent,
  ClipboardEvent,
  useMemo,
} from 'react';

// Define the props for the OtpInput component
type OtpInputProps = {
  /** The current value of the Code input. */
  value: string;
  /** Callback function that is called when the Code value changes. */
  onChange: (value: string) => void;
  /** The number of Code digits. Defaults to 6. */
  length?: number;
};

/**
 * A reusable and accessible Code input component built with React, TypeScript, and Tailwind CSS.
 * It supports keyboard navigation (arrow keys, backspace) and pasting.
 */
export default function OtpInput({ value, onChange, length = 6 }: OtpInputProps) {
  // Create an array of refs to hold references to each input element
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Focus the first input on component mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Memoize the array of digits to avoid re-creating it on every render
  const valueItems = useMemo(() => {
    const valueArray = value.split('');
    const items: Array<string> = [];
    for (let i = 0; i < length; i++) {
      items.push(valueArray[i] || '');
    }
    return items;
  }, [value, length]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const target = e.target;
    let targetValue = target.value.trim();
    const isTargetValueDigit = /^\d+$/.test(targetValue);

    // Prevent non-numeric characters from being entered
    if (!isTargetValueDigit && targetValue !== '') {
      return;
    }

    // Ensure only the last entered digit is kept
    targetValue = isTargetValueDigit ? targetValue.slice(-1) : '';

    // Update the Code value
    const newValue = valueItems.map((char, i) => (i === index ? targetValue : char)).join('');
    onChange(newValue);

    // If a digit is entered, move focus to the next input
    if (targetValue !== '' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    const { key } = e;
    const target = e.target as HTMLInputElement;

    if (key === 'ArrowRight') {
      // Move focus to the next input if it exists
      if (index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }

    if (key === 'ArrowLeft') {
      // Move focus to the previous input if it exists
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }

    if (key === 'Backspace') {
      // If the current input is empty, move focus to the previous input and clear it
      if (target.value === '' && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      // Clear the current input's value
      const newValue = valueItems.map((char, i) => (i === index ? '' : char)).join('');
      onChange(newValue);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData('text/plain')
      .trim()
      .slice(0, length)
      .replace(/[^0-9]/g, ''); // Allow only digits

    if (pastedData) {
      onChange(pastedData);
      // Focus the next empty input after pasting, or the last input if full
      const nextFocusIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[nextFocusIndex]?.focus();
    }
  };

  return (
    <div className="flex items-center justify-center gap-3">
      {valueItems.map((digit, index) => (
        <input
          key={index}
          ref={(el: HTMLInputElement | null) => {
            if (el) {
              inputRefs.current[index] = el;
            }
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={digit}
          onChange={(e) => handleInputChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={index === 0 ? handlePaste : undefined} // Only handle paste on the first input
          maxLength={1}
          className={clsx(
            'sm:w-12 sm:h-12 w-10 h-10 rounded-lg text-center text-2xl',
            'font-semibold text-gray-800 transition-all duration-200 ease-in-out focus:outline-none',
            { 'bg-[#E6FFFA] border border-[#20B2AA] ring-2 ring-[#20B2AA]/40 shadow': digit },
            { 'bg-white border border-gray-300 focus:border-[#20B2AA] focus:ring-2 focus:ring-[#20B2AA] shadow-sm': !digit },
          )}
          aria-label={`Code digit ${index + 1}`}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
};